import { type MarketplaceFees } from '@carsfinders/contracts';
import { useMutation, useQuery } from '@tanstack/react-query';
import { type ReactNode } from 'react';
import { useState } from 'react';

import { t } from '../../lib/i18n';
import { toast } from '../../lib/toast';
import { fetchWonCars } from '../won-cars/api';
import { formatCarDisplayName } from '../won-cars/utils';
import { createOwnStockListing, fetchMarketplaceFees } from './api';
import { previewFee } from './moderation-queue';

const CURRENCIES = ['EUR', 'USD', 'UAH', 'CHF'] as const;

/** Field shells shared by the two panels — label in mono caps, 40px control. */
const FIELD = 'h-10 w-full rounded-cf-md border border-cf-line bg-cf-surface px-3 text-sm';
const LABEL = 'mb-1 block font-mono text-[10px] tracking-[0.14em] text-cf-ink-subtle uppercase';

export interface OwnStockManagerProps {
  /**
   * Enqueue the freshly created listing for moderation — an own_stock listing
   * is created in `draft`, so it still has to travel draft → moderation →
   * active before buyers see it.
   */
  onListingCreated: (listingId: string) => void;
}

/**
 * Own-stock manager: turn a won auction car into a marketplace listing in one
 * click. Provenance (auction lot, AI-analysis ref, repair docs) is auto-filled
 * server-side from the won-car record — the admin only supplies the resale
 * price. Also renders the read-only consignment fee preview.
 *
 * The artboard names this tab («Власний склад») but draws no content for it, so
 * it has no geometry of its own to match. It is dressed in the same panel and
 * field language as the rest of Design D instead, so switching tabs does not
 * switch design systems mid-screen.
 */
export function OwnStockManager({ onListingCreated }: OwnStockManagerProps) {
  const [wonCarId, setWonCarId] = useState('');
  const [price, setPrice] = useState('');
  const [currency, setCurrency] = useState<string>('EUR');
  const [title, setTitle] = useState('');

  const wonCarsQuery = useQuery({ queryKey: ['won-cars'], queryFn: fetchWonCars });
  const feesQuery = useQuery({ queryKey: ['marketplace-fees'], queryFn: fetchMarketplaceFees });

  const createMutation = useMutation({
    mutationFn: () =>
      createOwnStockListing(wonCarId, {
        price: Number(price),
        currency,
        title: title.trim() || undefined,
      }),
    onSuccess: (result) => {
      const listingId = result.listing?.listingId ?? '';
      toast.success(t('marketplace.admin.ownStock.createSuccess', { id: listingId || '—' }));
      if (listingId) onListingCreated(listingId);
      setWonCarId('');
      setPrice('');
      setTitle('');
    },
    onError: (error: unknown) =>
      toast.error(
        t('marketplace.admin.ownStock.createError', {
          error: error instanceof Error ? error.message : 'unknown',
        }),
      ),
  });

  const parsedPrice = Number(price);
  const canCreate =
    wonCarId.trim().length > 0 && Number.isFinite(parsedPrice) && parsedPrice >= 0;

  const wonCars = wonCarsQuery.data ?? [];

  return (
    <>
      <Panel
        title={t('marketplace.admin.ownStock.title')}
        intro={t('marketplace.admin.ownStock.intro')}
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label htmlFor="own-stock-won-car" className={LABEL}>
              {t('marketplace.admin.ownStock.pickWonCar')}
            </label>
            <select
              id="own-stock-won-car"
              value={wonCarId}
              onChange={(event) => setWonCarId(event.target.value)}
              className={FIELD}
              disabled={wonCarsQuery.isPending}
            >
              <option value="">
                {wonCarsQuery.isPending
                  ? t('marketplace.admin.ownStock.loading')
                  : t('marketplace.admin.ownStock.empty')}
              </option>
              {wonCars.map((wonCar) => {
                const id = String(wonCar._id ?? '');
                if (!id) return null;
                return (
                  <option key={id} value={id}>
                    {formatCarDisplayName(wonCar.car ?? {})} — {id}
                  </option>
                );
              })}
            </select>
          </div>

          <div>
            <label htmlFor="own-stock-price" className={LABEL}>
              {t('marketplace.admin.ownStock.price')}
            </label>
            <input
              id="own-stock-price"
              type="number"
              min={0}
              value={price}
              onChange={(event) => setPrice(event.target.value)}
              className={`${FIELD} font-mono`}
            />
          </div>

          <div>
            <label htmlFor="own-stock-currency" className={LABEL}>
              {t('marketplace.admin.ownStock.currency')}
            </label>
            <select
              id="own-stock-currency"
              value={currency}
              onChange={(event) => setCurrency(event.target.value)}
              className={FIELD}
            >
              {CURRENCIES.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </div>

          <div className="sm:col-span-2">
            <label htmlFor="own-stock-title" className={LABEL}>
              {t('marketplace.admin.ownStock.listingTitle')}
            </label>
            <input
              id="own-stock-title"
              type="text"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              className={FIELD}
            />
          </div>
        </div>

        <button
          type="button"
          onClick={() => createMutation.mutate()}
          disabled={!canCreate || createMutation.isPending}
          className="mt-4 h-11 w-full rounded-cf-md bg-cf-success text-sm font-semibold text-white disabled:opacity-50 sm:w-auto sm:px-5 lg:h-10"
        >
          {createMutation.isPending
            ? t('marketplace.admin.ownStock.creating')
            : t('marketplace.admin.ownStock.create')}
        </button>
      </Panel>

      <FeePreview fees={feesQuery.data} isError={feesQuery.isError} />
    </>
  );
}

/**
 * Consignment fee preview — read-only. The fee is COMPUTED from the active
 * pricing ruleset but never settled here: settlement happens on the `sold`
 * transition and stays behind the Phase-13 payments gate.
 */
function FeePreview({ fees, isError }: { fees?: MarketplaceFees; isError: boolean }) {
  const [salePrice, setSalePrice] = useState('10000');

  if (!fees) return null;

  const resolved = previewFee(fees, Number(salePrice), 'consignment');
  const money = (value: number) =>
    new Intl.NumberFormat('uk-UA', { maximumFractionDigits: 2 }).format(value);

  const rows: Array<{ key: string; value: string }> = [
    {
      key: t('marketplace.admin.fee.feePct'),
      value: `${(fees.consignmentFeePct * 100).toFixed(1)}%`,
    },
    {
      key: t('marketplace.admin.fee.feeCap'),
      value:
        fees.consignmentFeeCap === null
          ? t('marketplace.admin.fee.uncapped')
          : money(fees.consignmentFeeCap),
    },
    { key: t('marketplace.admin.fee.listingFee'), value: money(fees.listingFee ?? 0) },
  ];

  return (
    <Panel title={t('marketplace.admin.fee.title')} intro={t('marketplace.admin.fee.intro')}>
      {isError && (
        <p className="mb-3 text-[13px] text-cf-warn">{t('marketplace.admin.fee.loadError')}</p>
      )}

      <div className="max-w-xs">
        <label htmlFor="fee-sale-price" className={LABEL}>
          {t('marketplace.admin.fee.salePrice')}
        </label>
        <input
          id="fee-sale-price"
          type="number"
          min={0}
          value={salePrice}
          onChange={(event) => setSalePrice(event.target.value)}
          className={`${FIELD} font-mono`}
        />
      </div>

      <div className="mt-4 flex flex-col">
        {rows.map((row) => (
          <div
            key={row.key}
            className="flex justify-between gap-2 border-b border-cf-line-soft py-2 text-[13px]"
          >
            <span className="text-cf-ink-muted">{row.key}</span>
            <span className="font-mono">{row.value}</span>
          </div>
        ))}
        <div className="flex items-baseline justify-between gap-2 py-2.5 text-[13px]">
          <span className="font-semibold text-cf-ink-muted">
            {t('marketplace.admin.fee.resolvedFee')}
          </span>
          <span className="font-mono text-[20px] font-medium" data-testid="fee-preview-resolved">
            {money(resolved)}
          </span>
        </div>
      </div>

      <p className="mt-3 text-xs leading-[1.4] text-cf-ink-subtle">
        {t('marketplace.admin.fee.gatedNote')}
      </p>
    </Panel>
  );
}

/** The screen's panel: hairline card, heading, one mono-caps intro line. */
function Panel({
  title,
  intro,
  children,
}: {
  title: string;
  intro: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-cf-lg border border-cf-line bg-cf-surface px-4 py-3.5 lg:px-5 lg:py-[18px]">
      <h2 className="text-base font-semibold tracking-[-0.02em] lg:text-[17px]">{title}</h2>
      <p className="mt-1 mb-4 text-xs leading-[1.4] text-cf-ink-subtle lg:text-[13px]">{intro}</p>
      {children}
    </section>
  );
}
